<template lang="pug">
  //- A full project, as rendered on /projects/:uid. Also mounted inline below the Selected Works slideshow (build brief B2).
  article#prj-mural.scroll-pt.bg-white.project-page(v-if="doc", :class="{'project-page--inline': inline}")
    //- header: split
    project-header(:doc="doc")
    //- summary
    section
      .px-12.my-48.md-my-72.md-flex.justify-end
        .px-12.mb-36.sm-w-12x18.lg-w-8x18.md-mb-0
          prismic-rich-text.type-body.fw-body.tracking-20.leading-wide.mb-em.children-mt-em(:field="doc.summary")
          prismic-rich-text.type-body.fw-body.tracking-20.leading-wide.text-gray-500(:field="doc.byline")
        aside.sm-w-3x18.lg-w-5x18.px-12.text-gray-500.child-links-underline.font-mono.type-meta
          <b>Share</b><br>
          .leading-loose.child-links-underline
            | <a :href="`mailto:?body=${shareUrl}`" target="_blank" rel="noopener">Email</a>,<br>
            | <a :href="`https://facebook.com/sharer.php?href=${shareUrl}`" target="_blank" rel="noopener">Facebook</a>,<br>
            | <a :href="`https://twitter.com/share?url=${shareUrl}`" target="_blank" rel="noopener">Twitter</a>
      //- gray details, bordered
      .px-24.my-36.md-mb-0.md-mt-72.font-mono.text-gray-500.type-meta
        .border-t.border-b.py-12.border-gray-200
          .w-grid-24.-ml-24.px-12.flex.md-block
            //- (product details header)
            .w-1x2.px-12.border-r.md-hidden
              h5 <b>Project Details</b>
            .w-1x2.sm-w-12x18.lg-w-8x18.md-flex.mx-auto.min-h-112
              .flex-1.px-12
                h6 <b>Project</b>
                small.block {{ txt(doc.title )}}
              .flex-1.px-12.md-border-l.md-border-r.border-gray-200
                h6 <b>Location</b>
                small.block {{ txt(doc.location) }}
              .flex-1.px-12
                h6 <b>Commissioner</b>
                small.block {{ txt(doc.commissioner) }}
    section#prj-body.scroll-pt
      project-body(:slices="doc.body", :footnotes="doc.footnotes")
    project-footer(:slices="doc.footer", :footnotes="doc.footnotes", :products="doc.related_products")
    //- next projects (existing end-of-project continuity: keep scrolling into the Gallery)
    observer.project__index.h-75vh.blur-media.pointer-events-none(:threshold="0.9", @visible="toIndex = true", @hidden="toIndex = false", :class="{'overflow-hidden': !leaving}")
      projects-by-gallery.pt-144(:fetch="true", :teaser="true")
    //- viewer (click to enlarge)
    transition(name="fade", v-on:before-enter="blurViewer = true", v-on:after-enter="blurViewer = false")
      project-viewer(:slides="mediaSlides", v-if="viewerAt", v-show="viewer", :class="{'blur-media': blurViewer}")
</template>

<script>
import ProjectHeader from '@/components/Project/ProjectHeader'
import ProjectBody from '@/components/Project/ProjectBody'
import ProjectFooter from '@/components/Project/ProjectFooter'
import ProjectViewer from '@/components/Project/Viewer/ProjectViewer'
import ProjectsByGallery from '@/components/ProjectsByGallery'
import Observer from '@/components/IntersectionObserver'
import { mapState } from 'vuex'
import { getNav, collectMedia } from '@/utils'

export default {
  name: 'ProjectPage',
  props: {
    uid: { type: String, required: true },
    inline: { type: Boolean, default: false }
  },
  data () {
    return {
      txt: this.$prismic.richTextAsPlain,
      toIndex: false,
      afterScroll: null,
      leaving: false,
      blurViewer: true
    }
  },
  computed: {
    ...mapState(['viewer', 'viewerAt', 'projects']),
    doc () {
      const doc = this.projects.find(doc => doc && doc.uid === this.uid)
      return doc && doc.data
    },
    mediaSlides () {
      return collectMedia(this.doc)
    },
    shareUrl () {
      return process.env.VUE_APP_DOMAIN + '/projects/' + this.uid
    }
  },
  methods: {
    async load () {
      // the route guard fetches for /projects/:uid; inline use fetches here
      if (!this.inline) return
      this.$store.commit('PROGRESS_START')
      try {
        await Promise.all([
          this.$store.dispatch('getProjects'),
          this.$store.dispatch('getProject', this.uid)
        ])
        const doc = this.doc
        const nav = doc && doc.scroll_nav_enabled !== 'hidden' && getNav(doc.body)
        this.$store.commit('SET_NAV', nav)
        this.$store.commit('PROGRESS_FINISH')
        this.$emit('loaded', !!doc)
      } catch (e) {
        console.error(e)
        this.$store.commit('PROGRESS_FAIL')
        this.$emit('loaded', false)
      }
    },
    onScroll () {
      clearTimeout(this.afterScroll)
      this.afterScroll = setTimeout(() => {
        if (!this.toIndex) return
        this.leaving = true
        this.$nextTick(() => this.$emit('onEndTo', 'projects'))
      }, 1000)
    }
  },
  watch: {
    uid: 'load',
    blurViewer (blur) {
      this.$store.commit('SET_BLUR', blur)
    }
  },
  created () {
    this.load()
  },
  mounted () {
    this.$store.state.bus.$on('newScrollY', this.onScroll)
  },
  destroyed () {
    this.$store.state.bus.$off('newScrollY', this.onScroll)
    this.$store.commit('RESET_VIEWER')
  },
  components: { ProjectHeader, ProjectBody, ProjectFooter, ProjectViewer, ProjectsByGallery, Observer }
}
</script>

<style>
</style>
